// Member bottom tab bar.
// iPhone (under 768px): Today, Training, Nutrition, Faith, and More. More opens
// the More page, which holds Community, Messages, the profile, and the rest.
// iPad (768px to the xl breakpoint): the five fixed destinations, where Me
// opens the personal community profile.
// Legacy custom-tab preferences remain stored, but do not override this navigation.
import { Link } from 'wouter';
import { Home, Dumbbell, Apple, Calendar, Users, UserCircle } from 'lucide-react';
import { clientBottomTargets, clientPhoneMoreActive, clientPhoneTabs, clientTabActive } from '@shared/clientNavigation';
import { CrossGlyph, ForkKnifeGlyph, KettlebellGlyph, MoreDotsGlyph, SunriseGlyph } from './TabIcons';
import '@/styles/iphone-shell.css';
const icons = { home: Home, training: Dumbbell, nutrition: Apple, schedule: Calendar, community: Users, me: UserCircle };
const phoneIcons = { today: SunriseGlyph, training: KettlebellGlyph, nutrition: ForkKnifeGlyph, faith: CrossGlyph };

type Props = {
  location: string;
  userId: number;
  inPerson: boolean;
  mapHref?: (href: string) => string;
  /** Renders the iPhone tab set. */
  phone?: boolean;
  moreOpen?: boolean;
  onMore?: () => void;
  /** Called when a route tab is tapped, so an open More page can close. */
  onNavigate?: () => void;
};

export function ClientCustomBottomNav({ location, userId, inPerson, mapHref = (href: string) => href, phone = false, moreOpen = false, onMore, onNavigate }: Props) {
  if (phone) {
    const moreActive = moreOpen || clientPhoneMoreActive(location);
    return <nav data-tour="bottom-nav" aria-label="Main navigation" data-phone-tabs="true"
      className="app-mobile-bottom-nav iphone-tabbar absolute bottom-0 left-0 right-0 z-50 flex min-w-0 items-stretch overflow-hidden glass-nav xl:hidden">
      {clientPhoneTabs().map(item => {
        const Glyph = phoneIcons[item.icon];
        const active = !moreOpen && clientTabActive(item.path, location);
        return <Link key={item.path} href={mapHref(item.path)} aria-current={active ? 'page' : undefined} onClick={onNavigate}
          className="iphone-tabbar__item flex min-w-0 flex-1 overflow-hidden" data-active={active ? 'true' : undefined}>
          <span className="iphone-tabbar__pill"><Glyph size={24} /></span>
          <span className="iphone-tabbar__label block max-w-full overflow-hidden text-ellipsis whitespace-nowrap">{item.label}</span>
        </Link>;
      })}
      <button type="button" onClick={onMore} aria-expanded={moreOpen} aria-current={moreActive && !moreOpen ? 'page' : undefined}
        className="iphone-tabbar__item flex min-w-0 flex-1 overflow-hidden" data-active={moreActive ? 'true' : undefined}>
        <span className="iphone-tabbar__pill"><MoreDotsGlyph size={24} /></span>
        <span className="iphone-tabbar__label block max-w-full overflow-hidden text-ellipsis whitespace-nowrap">More</span>
      </button>
    </nav>;
  }
  return <nav data-tour="bottom-nav" aria-label="Main navigation"
    className="app-mobile-bottom-nav absolute bottom-0 left-0 right-0 z-50 flex min-w-0 items-stretch overflow-hidden glass-nav md:left-1/2 md:right-auto md:bottom-4 md:w-[min(720px,calc(100vw-48px))] md:-translate-x-1/2 md:rounded-2xl md:border md:shadow-2xl xl:hidden"
    style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)', background: '#171715', borderTop: '1px solid rgba(178, 138, 73, 0.45)' }}>
    {clientBottomTargets(userId, inPerson).map(item => {
      const Icon = icons[item.icon as keyof typeof icons];
      const active = clientTabActive(item.path, location);
      return <Link key={item.path} href={mapHref(item.path)} aria-current={active ? 'page' : undefined}
        className="flex min-w-0 flex-1 overflow-hidden flex-col items-center justify-center gap-1 px-1 py-2 focus-visible:outline-2 focus-visible:outline-offset-[-3px] focus-visible:outline-[#b28a49]"
        style={{ minHeight: 58, color: active ? '#f0d49a' : '#c8c5bd', background: active ? 'rgba(178, 138, 73, 0.18)' : 'transparent', textDecoration: 'none' }}>
        <Icon className="h-5 w-5" strokeWidth={active ? 2.5 : 1.8} aria-hidden="true" />
        <span className="block max-w-full overflow-hidden text-ellipsis whitespace-nowrap" style={{ fontSize: 10, fontWeight: active ? 800 : 650, lineHeight: 1.3 }}>{item.label}</span>
      </Link>;
    })}
  </nav>;
}
